export interface Admin {
  id: string
  name: string
  email: string
  role: string
  department: string
  permissions: Record<string, boolean>
  status: "Active" | "Inactive" | "Suspended" | "In Review"
}

export const ROLE_OPTIONS: { value: string; label: string }[] = [
  { value: "teacher",            label: "Teacher" },
  { value: "principal",          label: "Principal" },
  { value: "vice_principal",     label: "Vice Principal" },
  { value: "hod",                label: "Head of Department" },
  { value: "accountant",         label: "Accountant" },
  { value: "librarian",          label: "Librarian" },
  { value: "counselor",          label: "Guidance Counselor" },
  { value: "lab_technician",     label: "Lab Technician" },
  { value: "sports_coordinator", label: "Sports Coordinator" },
  { value: "admin_officer",      label: "Admin Officer" },
  { value: "co_admin",           label: "Co-Admin (Full Access)" },
  { value: "other",              label: "Other" },
]

export const ROLE_LABELS: Record<string, string> = Object.fromEntries(
  ROLE_OPTIONS.map((r) => [r.value, r.label])
)

// Legacy string list kept for backward-compat
export const roles = ROLE_OPTIONS.map((r) => r.label)

export const departments = [
  "Sciences",
  "Arts",
  "Commercial",
  "Technical",
  "Languages",
  "Sports",
  "Administration",
  "Library",
  "Accounts",
  "Guidance & Counseling",
]

export interface PermissionDef {
  key: string
  label: string
  description: string
  group: string
}

export const PERMISSIONS: PermissionDef[] = [
  { key: "canViewStudents",         label: "View Students",          description: "See student profiles and basic info",        group: "Students" },
  { key: "canEditStudents",         label: "Edit Students",          description: "Update student records and details",         group: "Students" },
  { key: "canDeleteStudents",       label: "Delete Students",        description: "Remove student accounts",                    group: "Students" },
  { key: "canViewStaff",            label: "View Staff",             description: "See staff members and their profiles",       group: "Staff" },
  { key: "canEditStaff",            label: "Edit Staff",             description: "Update staff records and roles",             group: "Staff" },
  { key: "canManageSubjects",       label: "Manage Subjects",        description: "Create, edit and delete subjects",           group: "Academics" },
  { key: "canManageClasses",        label: "Manage Classes",         description: "Create and configure class groups",          group: "Academics" },
  { key: "canManageAssessments",    label: "Manage Assessments",     description: "Create and manage tests and assessments",    group: "Academics" },
  { key: "canViewGrades",           label: "View Grades",            description: "See student grades and results",             group: "Academics" },
  { key: "canEditGrades",           label: "Edit Grades",            description: "Update and verify student grades",           group: "Academics" },
  { key: "canManageCurriculum",     label: "Manage Curriculum",      description: "Edit learning paths and topic content",      group: "Academics" },
  { key: "canViewReports",          label: "View Reports",           description: "Access analytics and report dashboards",     group: "Reports" },
  { key: "canGenerateReports",      label: "Generate Reports",       description: "Export and download student/class reports",  group: "Reports" },
  { key: "canManageFees",           label: "Manage Fees",            description: "Handle fee records and payments",            group: "Finance" },
  { key: "canViewFinancialReports", label: "View Financial Reports", description: "Access fee and financial summaries",         group: "Finance" },
  { key: "canManageLibrary",        label: "Manage Library",         description: "Add and manage library resources",           group: "Resources" },
  { key: "canManageInventory",      label: "Manage Inventory",       description: "Track and manage school inventory",          group: "Resources" },
  { key: "canSendNotifications",    label: "Send Notifications",     description: "Broadcast messages to students and parents", group: "Communication" },
  { key: "canManageEvents",         label: "Manage Events",          description: "Create and publish school events",           group: "Events" },
  { key: "canManageSystemSettings", label: "Manage System Settings", description: "Change school-wide configuration",           group: "System" },
]

export const PERMISSION_GROUPS = [...new Set(PERMISSIONS.map((p) => p.group))]

// Legacy flat list kept for backward-compat
export const availablePermissions = PERMISSIONS.map((p) => p.label)

/** Default permission set per backend role value */
export const ROLE_PERMISSION_PRESETS: Record<string, Record<string, boolean>> = {
  teacher: {
    canViewStudents: true, canViewGrades: true, canEditGrades: true,
    canManageAssessments: true, canViewReports: true,
  },
  principal: {
    canViewStudents: true, canEditStudents: true,
    canViewStaff: true, canEditStaff: true,
    canManageSubjects: true, canManageClasses: true,
    canManageAssessments: true, canViewGrades: true, canEditGrades: true,
    canManageCurriculum: true, canViewReports: true, canGenerateReports: true,
    canManageEvents: true, canSendNotifications: true, canManageSystemSettings: true,
  },
  vice_principal: {
    canViewStudents: true, canEditStudents: true, canViewStaff: true,
    canManageSubjects: true, canManageClasses: true,
    canManageAssessments: true, canViewGrades: true, canEditGrades: true,
    canViewReports: true, canGenerateReports: true,
    canManageEvents: true, canSendNotifications: true,
  },
  hod: {
    canViewStudents: true, canManageSubjects: true, canManageAssessments: true,
    canViewGrades: true, canEditGrades: true, canManageCurriculum: true, canViewReports: true,
  },
  accountant: {
    canViewStudents: true, canManageFees: true,
    canViewFinancialReports: true, canGenerateReports: true,
  },
  librarian: {
    canViewStudents: true, canManageLibrary: true, canManageInventory: true,
  },
  counselor: {
    canViewStudents: true, canViewReports: true, canSendNotifications: true,
  },
  lab_technician: {
    canViewStudents: true, canManageInventory: true,
  },
  sports_coordinator: {
    canViewStudents: true, canManageEvents: true,
  },
  admin_officer: {
    canViewStudents: true, canEditStudents: true, canViewStaff: true,
    canManageClasses: true, canViewReports: true, canGenerateReports: true,
    canManageEvents: true, canSendNotifications: true, canManageSystemSettings: true,
  },
  co_admin: Object.fromEntries(PERMISSIONS.map((p) => [p.key, true])),
  other: { canViewStudents: true },
}

/** Build a full permissions object from backend data (defaults all missing keys to false) */
export function normalizePermissions(perms: Record<string, boolean> = {}): Record<string, boolean> {
  const obj: Record<string, boolean> = {}
  PERMISSIONS.forEach((p) => { obj[p.key] = perms[p.key] ?? false })
  return obj
}

export const initialAdmins: Admin[] = []
